import React from 'react';
import { CheckCircleOutlined } from '@ant-design/icons';

interface VisionLevel {
  level: string;
  title: string;
  description: string;
  status: 'current' | 'developing' | 'future';
  features: string[];
  timeline: string;
}

const VisionSection: React.FC = () => {
  const visionLevels: VisionLevel[] = [
    {
      level: "L2",
      title: "AI工具辅助人类进行软件开发",
      description: "基于丰富的基础设施，通过Cursor、Claude Code等AI编码工具，我们可以快速开发出高品质APP和应用。",
      status: "current",
      features: [
        "平台提供扎实的基础架构和技术体系，避免重复写大量高难度的框架代码。",
        "通过Cursor、Claude Code等AI工具快速开发，一个简单的APP甚至一天就可以完成。",
        "项目定期定时进行可视化交付，大项目可每日演示，提前发现风险，避免陷入忙尾效应。",
        "性能秒杀99%的互联网公司，让高品质的软件遍地开花，即便是卖包子的软件也能获得极速体验！"
      ],
      timeline: "当前阶段"
    },
    {
      level: "L3",
      title: "人类监督下的AI自主软件开发",
      description: "AI直接面向客户了解需求，开展工作，当发生异常或者陷入停滞时，人类开发者可以随时接管。",
      status: "developing",
      features: [
        "客户通过圆领悟空平台直接与AI产品经理对话，说出自己的需求，AI会引导客户进行细化和确认需求。",
        "UI设计师AI会根据需求设计出UI，通过UI设计师真人确认和微调，与客户视频会议沟通确认UI。",
        "AI架构师会根据需求设计出架构，拆分编码任务，细节经过真人架构师确认后，移交给AI前端和AI后端进行编码。",
        "AI前端和AI后端会根据需求进行编码，编码完成后，经过真人产品经理确认后，发布到悟空平台让客户体验并确认。",
      ],
      timeline: "开发中"
    },
    {
      level: "L4-L5",
      title: "人类轻度维护下的AI自主软件开发",
      description: "随着AGI技术革新，一个开发者/产品经理/UI设计师将可以管理数十上百个项目，人类只需要轻度维护即可。",
      status: "future",
      features: [
        "AI产品经理已经可以非常流畅和体贴的与客户进行沟通，提出自己的意见，并引导客户进行细化和确认需求。",
        "UI设计师可以自己与客户进行视频对话并在特定工具上修改设计，并实时反馈给客户，而无需人类干预。",
        "AI架构师总是能精确的根据需求设计出合理的架构，拆分出合理的编码任务，并分配给AI前端和AI后端进行编码，而无需人类干预。",
        "AI程序员可以进行99%场景下的自主编码，自主修复问题，并自动发布到悟空平台让客户体验并确认。",
      ],
      timeline: "未来愿景"
    }
  ];

  const sectionStyle: React.CSSProperties = {
    position: 'relative',
    padding: '3rem 0 8rem 0',
    background: '#000000',
    overflow: 'hidden',
  };

  const containerStyle: React.CSSProperties = {
    maxWidth: '1000px',
    margin: '0 auto',
    marginTop: '0rem',
    padding: '0 2rem',
    paddingTop: '0rem',
    position: 'relative',
    zIndex: 2,
  };

  const headerStyle: React.CSSProperties = {
    textAlign: 'center',
    marginBottom: '5rem',
  };

  const titleStyle: React.CSSProperties = {
    fontSize: '2rem',
    fontWeight: 700,
    color: '#ffffff',
    marginBottom: '1rem',
    background: 'linear-gradient(135deg, #fbbf24 0%, #f59e0b 50%, #d97706 100%)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundClip: 'text',
  };

  const subtitleStyle: React.CSSProperties = {
    fontSize: '1.2rem',
    color: '#94a3b8',
    fontWeight: 400,
    marginBottom: '1rem',
  };

  const missionStyle: React.CSSProperties = {
    fontSize: '1rem',
    color: '#cbd5e1',
    fontWeight: 300,
    fontStyle: 'italic',
  };

  const timelineStyle: React.CSSProperties = {
    position: 'relative',
    paddingLeft: '2rem',
  };

  const timelineLineStyle: React.CSSProperties = {
    position: 'absolute',
    left: '1rem',
    top: '0',
    bottom: '0',
    width: '2px',
    background: 'linear-gradient(180deg, #60a5fa 0%, #a855f7 50%, #fbbf24 100%)',
  };

  const levelCardStyle = (status: string): React.CSSProperties => ({
    position: 'relative',
    marginBottom: '3rem',
    paddingLeft: '2rem',
  });

  const levelDotStyle = (status: string): React.CSSProperties => {
    let color = '#94a3b8';
    let size = '12px';
    
    if (status === 'current') {
      color = '#60a5fa';
      size = '16px';
    } else if (status === 'developing') {
      color = '#a855f7';
      size = '14px';
    } else if (status === 'future') {
      color = '#fbbf24';
      size = '12px';
    }

    return {
      position: 'absolute',
      left: '-6px',
      top: '1.5rem',
      width: size,
      height: size,
      backgroundColor: color,
      borderRadius: '50%',
      border: '3px solid #1e293b',
      zIndex: 3,
      boxShadow: `0 0 20px ${color}`,
    };
  };

  const cardContentStyle: React.CSSProperties = {
    background: 'rgba(255, 255, 255, 0.03)',
    backdropFilter: 'blur(10px)',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    borderRadius: '16px',
    padding: '2rem',
    transition: 'all 0.3s ease',
  };

  const levelBadgeStyle = (status: string): React.CSSProperties => {
    let bgColor = 'rgba(148, 163, 184, 0.2)';
    let textColor = '#94a3b8';
    
    if (status === 'current') {
      bgColor = 'rgba(96, 165, 250, 0.2)';
      textColor = '#60a5fa';
    } else if (status === 'developing') {
      bgColor = 'rgba(168, 85, 247, 0.2)';
      textColor = '#a855f7';
    } else if (status === 'future') {
      bgColor = 'rgba(251, 191, 36, 0.2)';
      textColor = '#fbbf24';
    }

    return {
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '0.5rem',
      backgroundColor: bgColor,
      color: textColor,
      padding: '0.4rem 0.8rem',
      borderRadius: '20px',
      fontSize: '0.8rem',
      fontWeight: 500,
      marginBottom: '0',
      height: 'fit-content',
    };
  };

  const levelTitleStyle: React.CSSProperties = {
    fontSize: '1.5rem',
    fontWeight: 600,
    color: '#ffffff',
    marginBottom: '0.5rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '1rem',
  };

  const levelNumberStyle: React.CSSProperties = {
    fontSize: '2rem',
    fontWeight: 700,
    background: 'linear-gradient(135deg, #60a5fa 0%, #a855f7 100%)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundClip: 'text',
  };

  const levelDescStyle: React.CSSProperties = {
    fontSize: '1rem',
    color: '#94a3b8',
    marginBottom: '1.5rem',
    lineHeight: 1.6,
  };

  const featureListStyle: React.CSSProperties = {
    listStyle: 'none',
    padding: 0,
    margin: 0,
  };

  const featureItemStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'flex-start',
    marginBottom: '0.75rem',
    fontSize: '0.9rem',
    color: '#cbd5e1',
  };

  const featureIconStyle: React.CSSProperties = {
    marginRight: '0.6rem',
    marginTop: '0.2rem',
    fontSize: '0.8rem',
    flexShrink: 0,
    color: '#60a5fa',
  };
 

  return (
    <>
      <style>
        {`
          .level-card {
            transition: all 0.3s ease;
          }
          
          .level-card:hover .card-content {
            background: rgba(255, 255, 255, 0.06) !important;
            border-color: rgba(96, 165, 250, 0.2) !important;
            transform: translateX(0.5rem);
          }

          @media (max-width: 768px) {
            .vision-title {
              font-size: 2.5rem !important;
            }
            
            .vision-subtitle {
              font-size: 1.25rem !important;
            }
            
            .timeline {
              padding-left: 1.5rem !important;
            }
            
            .level-card {
              padding-left: 1.5rem !important;
            }
            
            .card-content {
              padding: 1.5rem !important;
            }
          }

          @media (max-width: 480px) {
            .vision-title {
              font-size: 2rem !important;
            }
            
            .vision-container {
              padding: 0 1rem !important;
            }
            
            .timeline {
              padding-left: 1rem !important;
            }
            
            .level-card {
              padding-left: 1rem !important;
            }
          }
        `}
      </style>

      <section style={sectionStyle}> 
        <div style={containerStyle} className="vision-container">
          <div style={headerStyle}>
            <h2 style={titleStyle} className="vision-title">
              圆领·悟空AI软件开发白皮书
            </h2>
            <p style={subtitleStyle} className="vision-subtitle">
              真正让完全不会代码的客户也能开发出高品质软件
            </p>
            <p style={missionStyle}>
              当下即便是最智能的AI编程工具，都有着极其高的实用门槛，非专业人士寸步难行。
            </p>
          </div>

          <div style={timelineStyle} className="timeline">
            <div style={timelineLineStyle}></div>
            
            {visionLevels.map((level, index) => (
              <div key={index} style={levelCardStyle(level.status)} className="level-card">
                <div style={levelDotStyle(level.status)}></div>
                
                <div style={cardContentStyle} className="card-content">
                  <div style={levelTitleStyle}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                      <span style={levelNumberStyle}>{level.level}</span>
                      <span>{level.title}</span>
                    </div>
                    <div style={levelBadgeStyle(level.status)}>
                      <span>{level.timeline}</span>
                    </div>
                  </div>
                  
                  <p style={levelDescStyle}>{level.description}</p>
                  
                  <ul style={featureListStyle}>
                    {level.features.map((feature, featureIndex) => (
                      <li key={featureIndex} style={featureItemStyle}>
                        <CheckCircleOutlined style={featureIconStyle} />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
};

export default VisionSection; 